import { useState, useCallback } from 'react'
import { router } from '@inertiajs/react'
import { AISettings } from '@/components/settings'
import type { AIConfig, Provider, AIMode, ProviderId, ValidationState } from '@/components/settings'

interface Props {
  config: AIConfig
  providers: Provider[]
}

export default function SettingsIndex({ config: initialConfig, providers }: Props) {
  const [config, setConfig] = useState<AIConfig>(initialConfig)
  const [apiKey, setApiKey] = useState('')
  const [validationState, setValidationState] = useState<ValidationState>({
    status: initialConfig.validationStatus === 'valid' ? 'valid' : 'idle',
    message: null,
  })

  const handleModeChange = useCallback((mode: AIMode) => {
    setConfig(prev => ({ ...prev, mode }))

    // Save mode change to backend
    fetch('/ai_config', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      },
      body: JSON.stringify({ ai_config: { mode } }),
    })
  }, [])

  const handleProviderChange = useCallback((provider: ProviderId) => {
    const selectedProvider = providers.find(p => p.id === provider)
    const firstModel = selectedProvider?.models[0]?.id || null

    setConfig(prev => ({ ...prev, provider, validationStatus: 'idle', selectedModel: firstModel }))
    setValidationState({ status: 'idle', message: null })

    // Save provider and model change to backend
    fetch('/ai_config', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      },
      body: JSON.stringify({ ai_config: { provider, selected_model: firstModel } }),
    })
  }, [providers])

  const handleApiKeyChange = useCallback((newApiKey: string) => {
    setApiKey(newApiKey)
  }, [])

  const handleSaveApiKey = useCallback(async () => {
    if (!apiKey || !config.provider) return

    setValidationState({ status: 'validating', message: 'Verifying API key...' })

    try {
      const response = await fetch('/ai_config/validate_api_key', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({ provider: config.provider, api_key: apiKey }),
      })

      const result = await response.json()

      if (result.status === 'valid') {
        setValidationState({ status: 'valid', message: result.message })
        setConfig(prev => ({ ...prev, validationStatus: 'valid', apiKey: '••••••••' }))
      } else {
        setValidationState({ status: result.status, message: result.message })
        setConfig(prev => ({ ...prev, validationStatus: result.status }))
      }
    } catch (error) {
      setValidationState({ status: 'error', message: 'Failed to validate API key' })
    }
  }, [apiKey, config.provider])

  const handleModelChange = useCallback((modelId: string) => {
    setConfig(prev => ({ ...prev, selectedModel: modelId }))

    // Save model change to backend
    fetch('/ai_config', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      },
      body: JSON.stringify({ ai_config: { selected_model: modelId } }),
    })
  }, [])

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      <header className="bg-white dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
        <div className="max-w-5xl mx-auto px-6 py-5">
          <h1 className="text-xl font-semibold text-stone-900 dark:text-stone-100">
            Settings
          </h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-6">
        <AISettings
          config={config}
          providers={providers}
          onModeChange={handleModeChange}
          onProviderChange={handleProviderChange}
          onApiKeyChange={handleApiKeyChange}
          onSaveApiKey={handleSaveApiKey}
          onModelChange={handleModelChange}
          validationState={validationState}
        />
      </main>
    </div>
  )
}
